/**
 * PIRequirementsBanner Component
 * Simple contextual tip showing the most important thing to know/do next
 * 
 * Easy to remove: Just delete this file and remove the import/usage from PICalculator.tsx
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Lightbulb } from 'lucide-react';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import type { PICalculatorData, PICalculatedScore } from '@/lib/pi-calculator-utils';

interface PIRequirementsBannerProps {
  data: PICalculatorData;
  score: PICalculatedScore;
}

function getContextualTip(data: PICalculatorData, score: PICalculatedScore, programYear: number): string | null {
  // Priority 1: Performance period issues
  if (!data.performance_start || !data.performance_end) {
    return `Set your performance period in Section 1. You need at least 180 consecutive days, starting no later than July 5, ${programYear}.`;
  }

  // Check if period is at least 180 days
  const start = new Date(data.performance_start);
  const end = new Date(data.performance_end);
  const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 180) {
    return `Your performance period is ${days} days. You need at least 180 consecutive days for PI reporting.`;
  }

  // Priority 2: CEHRT ID missing
  if (!data.cehrt_id || data.cehrt_id.trim().length === 0) {
    return 'Enter your CEHRT ID in Section 1. This is required for CMS submission.';
  }

  // Priority 3: Prerequisites failed (any "No" answer)
  const prereqsFailed = 
    data.pi_pphi_1 === false ||
    data.pi_pphi_2 === false ||
    data.pi_infblo_1 === false ||
    data.pi_oncdir_1 === false;

  if (prereqsFailed) {
    return 'One or more prerequisite attestations is "No". Your PI score will be 0 points until all are answered "Yes".';
  }

  // Priority 4: Prerequisites incomplete
  const prereqsComplete = 
    data.pi_pphi_1 === true &&
    data.pi_pphi_2 === true &&
    data.pi_infblo_1 === true &&
    data.pi_oncdir_1 === true;

  if (!prereqsComplete) {
    return 'Complete all 4 prerequisite attestations in Section 2. Each must be "Yes" to earn any PI points.';
  }

  // Priority 5: Objectives guidance
  if (score.totalScore === 0) {
    return 'Prerequisites complete. Now report your measures or claim exclusions in the objectives below.';
  }

  if (score.totalScore < 100) {
    return 'You\'re making progress. Continue completing objectives to maximize your PI score.';
  }

  // All complete
  return null; // No tip needed when everything is done
}

// ============================================================================
// DESIGN SELECTION
// Set PREVIEW_ALL = true to see all designs with tabs, false to use selected
// Available designs: 1 (subtle card), 5 (dark), 9 (split), 10 (minimal underline)
// ============================================================================
const PREVIEW_ALL = false;
const SELECTED_DESIGN = 10;

export function PIRequirementsBanner({ data, score }: PIRequirementsBannerProps) {
  const { programYear } = useProgramYear();
  const [activeTab, setActiveTab] = useState<number>(1);
  const tip = getContextualTip(data, score, programYear);

  // Don't render if no tip needed (everything complete)
  if (!tip) return null;

  // Design components (only keeping 1, 5, 9, 10)
  const Design1 = (
    <Card className="bg-slate-50/50">
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-3">
          <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-gray-700">{tip}</p>
        </div>
      </CardContent>
    </Card>
  );

  const Design5 = (
    <div className="rounded-lg bg-slate-800 px-4 py-3">
      <div className="flex items-center gap-3">
        <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0" />
        <p className="text-sm text-white">{tip}</p>
      </div>
    </div>
  );

  const Design9 = (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden flex">
      <div className="w-12 bg-amber-500 flex items-center justify-center flex-shrink-0">
        <Lightbulb className="w-5 h-5 text-white" />
      </div>
      <div className="px-4 py-3 flex-1">
        <p className="text-sm text-gray-800">{tip}</p>
      </div>
    </div>
  );

  const Design10 = (
    <div className="border-b-2 border-primary pb-3 mb-1">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-primary uppercase tracking-wide">Next Step</span>
      </div>
      <p className="text-sm text-gray-700 mt-1">{tip}</p>
    </div>
  );

  // Show all designs in preview mode
  if (PREVIEW_ALL) {
    const tabs = [
      { id: 1, label: '1' },
      { id: 5, label: '5' },
      { id: 9, label: '9' },
      { id: 10, label: '10' },
    ];

    const getDesign = () => {
      switch (activeTab) {
        case 1: return Design1;
        case 5: return Design5;
        case 9: return Design9;
        case 10: return Design10;
        default: return Design1;
      }
    };

    return (
      <div className="space-y-2">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {getDesign()}
      </div>
    );
  }

  // Return selected design
  const designs: Record<number, JSX.Element> = { 1: Design1, 5: Design5, 9: Design9, 10: Design10 };
  return designs[SELECTED_DESIGN] || Design1;
}
